// TIP-609 — the claim gate: the two states that sit IN FRONT of the whole
// product. Not a variant of the allowance card — a fork above it. When this
// renders, neither the allowance card nor the briefs rows exist.
//
// One layout, one DOM, two variants: every string swaps, and the accent colour
// follows data-variant in the stylesheet.
//   unclaimed → orange accent, three numbered steps
//   pending   → gold accent, two ticks and one live step
//
// Copy is taken from Counterparty Briefs.dc.html, whose `tier` demo enum
// carries Unclaimed and Claim pending. There is deliberately no
// rejected-claim state: the ladder is Unclaimed → Pending → Claimed → Member
// and a declined claim is still undecided.
import React from "react";
import "./counterpartyBriefs.css";
import { ReactComponent as GateIllustration } from "../../../../assets/icons/PurchaseCompany.svg";
import { BusinessRow } from "./AllowanceCard";
import CbPanel from "./CbPanel";
import CbButton from "./CbButton";
import CbPill from "./CbPill";
import CounterDivider from "./CounterDivider";
import BriefsTableShell, { EmptyRow } from "./BriefsTableShell";
import { GATE_COPY, GATE_ROUTES, ENTITLEMENTS } from "./briefsFixtures";

const LockedStrip = ({ lockNote }) => (
  <div data-testid="cb-gate-locked-strip" className="cb-locked">
    <div className="cb-locked-note">
      <svg className="cb-locked-icon" width="14" height="15" viewBox="0 0 14 15" fill="none" aria-hidden="true">
        <rect x="2.2" y="6.4" width="9.6" height="7.2" rx="1.6" stroke="currentColor" strokeWidth="1.4" />
        <path d="M4.6 6.4V4.6a2.4 2.4 0 0 1 4.8 0v1.8" stroke="currentColor" strokeWidth="1.4" />
      </svg>
      <span>{lockNote}</span>
    </div>

    {/* Every figure greyed: a preview of what is not yours yet. The numbers
        read from ENTITLEMENTS, so the panel cannot go stale. */}
    <div className="cb-gate-numbers">
      {GATE_COPY.lockedStrip.map((item, i) => (
        <React.Fragment key={item.eyebrow}>
          {i > 0 && <CounterDivider />}
          <div>
            <span className="cb-locked-eyebrow">{item.eyebrow}</span>
            <p className="cb-locked-value">{ENTITLEMENTS.claimed[item.key]}</p>
            <p className="cb-locked-caption">{item.note}</p>
          </div>
        </React.Fragment>
      ))}
    </div>
  </div>
);

const GateTableShell = ({ tableNote }) => (
  <CbPanel variant="gate-table" flush>
    <div className="cb-gate-table-head">
      <h3 className="cb-gate-table-title">{GATE_COPY.tableHeading}</h3>
    </div>

    <BriefsTableShell columns={GATE_COPY.tableColumns} gate>
      <EmptyRow
        gate
        colSpan={GATE_COPY.tableColumns.length}
        heading={GATE_COPY.tableEmptyHeading}
        body={tableNote}
      />
    </BriefsTableShell>
  </CbPanel>
);

const ClaimGate = ({ variant, onNavigate }) => {
  const v = GATE_COPY[variant] || GATE_COPY.unclaimed;
  const routes = variant === "pending"
    ? [GATE_ROUTES.claimStatus, GATE_ROUTES.sendDocument]
    : [GATE_ROUTES.findBusiness, GATE_ROUTES.whatIsABrief];

  return (
    <>
      {/* The sharing note is suppressed: nothing is claimed, so there is no
          shared allowance to describe. The design renders it regardless. */}
      <BusinessRow
        name={v.headCompany}
        note={null}
        pill={<CbPill testId="cb-gate-pill" variant={variant} label={v.pillLabel} />}
      />

      <CbPanel data-testid="cb-claim-gate" data-variant={variant} variant="gate">
        <div className="cb-gate-head">
          <div className="cb-gate-copy">
            <p className="cb-gate-eyebrow">{v.eyebrow}</p>
            <h3 className="cb-gate-title">{v.title}</h3>
            <p className="cb-gate-body">{v.body}</p>
          </div>
          <GateIllustration aria-hidden="true" className="cb-gate-art" />
        </div>

        <LockedStrip lockNote={v.lockNote} />

        <div className="cb-gate-steps">
          {v.steps.map((s) => (
            <div key={s.title} className="cb-gate-step">
              <span
                data-testid={s.state === "now" ? "cb-gate-step-current" : undefined}
                className={`cb-gate-step-mark cb-gate-step-mark--${s.state}`}
              >
                {s.n}
              </span>
              <div>
                <p className="cb-gate-step-title">{s.title}</p>
                <p className="cb-gate-step-body">{s.body}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="cb-gate-actions">
          <CbButton onClick={() => onNavigate(routes[0])}>{v.cta}</CbButton>
          <CbButton variant="secondary" light onClick={() => onNavigate(routes[1])}>{v.ctaTwo}</CbButton>
        </div>
      </CbPanel>

      <GateTableShell tableNote={v.tableNote} />
    </>
  );
};

export default ClaimGate;
